import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  fetchJobSeekerApplications, 
  getJobSeekerApplicationStatistics,
  getApplicationStatusHistory
} from '../../../services/applicationsService';
import { getApplicationAnalytics, createInterviewSchedule, getApplicationNotes, createApplicationNote, deleteApplicationNote, getApplicationReminders, createApplicationReminder, updateApplicationReminder, deleteApplicationReminder } from '../../../services/applicationHistoryService';
import { Briefcase, MapPin, Calendar, Building2, Filter, TrendingUp, Clock, CheckCircle, XCircle, FileText, Eye, History, Video, Phone, StickyNote, Bell, Plus, Trash2, Save, X, CalendarClock, Target, GitCompare } from 'lucide-react';
import Icon from '../../../components/AppIcon';


// Status configuration with colors and icons
const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    icon: Clock,
    description: 'Application submitted and awaiting review'
  },
  reviewed: {
    label: 'Reviewed',
    color: 'bg-blue-100 text-blue-800 border-blue-200',
    icon: Eye,
    description: 'Application has been reviewed by employer'
  },
  interview: {
    label: 'Interview',
    color: 'bg-purple-100 text-purple-800 border-purple-200',
    icon: Calendar,
    description: 'Interview scheduled or in progress'
  },
  offer: {
    label: 'Offer',
    color: 'bg-green-100 text-green-800 border-green-200',
    icon: TrendingUp,
    description: 'Job offer extended'
  },
  accepted: {
    label: 'Accepted',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    icon: CheckCircle,
    description: 'Offer accepted'
  },
  rejected: {
    label: 'Rejected',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: XCircle,
    description: 'Application rejected'
  }
};

// Enhanced Analytics Dashboard Component
const AnalyticsDashboard = ({ analytics, statistics }) => {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Response Rate */}
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Response Rate</span>
            <Target className="w-5 h-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-foreground">{analytics?.responseRate?.toFixed(1)}%</p>
          <p className="text-xs text-muted-foreground mt-1">
            {statistics?.byStatus?.reviewed + statistics?.byStatus?.interview + statistics?.byStatus?.offer || 0} responses
          </p>
        </div>

        {/* Interview Rate */}
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Interview Rate</span>
            <CalendarClock className="w-5 h-5 text-purple-600" />
          </div>
          <p className="text-2xl font-bold text-foreground">{analytics?.interviewRate?.toFixed(1)}%</p>
          <p className="text-xs text-muted-foreground mt-1">
            {statistics?.byStatus?.interview || 0} interviews
          </p>
        </div>

        {/* Avg Response Time */}
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Avg Response Time</span>
            <Clock className="w-5 h-5 text-orange-600" />
          </div>
          <p className="text-2xl font-bold text-foreground">{analytics?.avgResponseTimeDays || 0} days</p>
          <p className="text-xs text-muted-foreground mt-1">
            Fastest: {analytics?.fastestResponseDays || 0}d, Slowest: {analytics?.slowestResponseDays || 0}d
          </p>
        </div>

        {/* Offer Rate */}
        <div className="bg-background border border-border rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-muted-foreground">Offer Rate</span>
            <TrendingUp className="w-5 h-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-foreground">{analytics?.offerRate?.toFixed(1)}%</p>
          <p className="text-xs text-muted-foreground mt-1">
            {statistics?.byStatus?.offer + statistics?.byStatus?.accepted || 0} offers
          </p>
        </div>
      </div>
    </div>
  );
};

// Interview Schedule Modal Component
const InterviewScheduleModal = ({ application, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    interviewType: 'video',
    interviewDate: '',
    interviewDurationMinutes: 60,
    location: '',
    meetingLink: '',
    interviewerName: '',
    interviewerEmail: '',
    preparationNotes: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await createInterviewSchedule({
        applicationId: application?.id,
        ...formData
      });
      onSave();
      onClose();
    } catch (error) {
      console.error('Failed to create interview schedule:', error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Schedule Interview</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {application?.jobs?.title} at {application?.jobs?.companies?.name}
              </p>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          <div className="space-y-4">
            {/* Interview Type */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Interview Type</label>
              <select
                value={formData?.interviewType}
                onChange={(e) => setFormData({ ...formData, interviewType: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                required
              >
                <option value="phone">Phone Interview</option>
                <option value="video">Video Interview</option>
                <option value="in_person">In-Person Interview</option>
                <option value="technical">Technical Interview</option>
                <option value="behavioral">Behavioral Interview</option>
                <option value="final">Final Interview</option>
              </select>
            </div>

            {/* Date and Duration */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Interview Date & Time</label>
                <input
                  type="datetime-local"
                  value={formData?.interviewDate}
                  onChange={(e) => setFormData({ ...formData, interviewDate: e?.target?.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Duration (minutes)</label>
                <input
                  type="number"
                  value={formData?.interviewDurationMinutes}
                  onChange={(e) => setFormData({ ...formData, interviewDurationMinutes: parseInt(e?.target?.value) })}
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                  min="15"
                  step="15"
                  required
                />
              </div>
            </div>

            {/* Location or Meeting Link */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {formData?.interviewType === 'in_person' ? 'Location' : 'Meeting Link'}
              </label>
              <input
                type={formData?.interviewType === 'in_person' ? 'text' : 'url'}
                value={formData?.interviewType === 'in_person' ? formData?.location : formData?.meetingLink}
                onChange={(e) => setFormData({
                  ...formData,
                  [formData?.interviewType === 'in_person' ? 'location' : 'meetingLink']: e?.target?.value
                })}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                placeholder={formData?.interviewType === 'in_person' ? 'Enter office address' : 'https://meet.google.com/...'}
              />
            </div>

            {/* Interviewer Details */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Interviewer Name</label>
                <input
                  type="text"
                  value={formData?.interviewerName}
                  onChange={(e) => setFormData({ ...formData, interviewerName: e?.target?.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                  placeholder="Jane Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">Interviewer Email</label>
                <input
                  type="email"
                  value={formData?.interviewerEmail}
                  onChange={(e) => setFormData({ ...formData, interviewerEmail: e?.target?.value })}
                  className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                  placeholder="jane@company.com"
                />
              </div>
            </div>

            {/* Preparation Notes */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Preparation Notes</label>
              <textarea
                value={formData?.preparationNotes}
                onChange={(e) => setFormData({ ...formData, preparationNotes: e?.target?.value })}
                className="w-full px-4 py-2 border border-border rounded-lg bg-background text-foreground"
                rows={4}
                placeholder="Key points to prepare, research items, questions to ask..."
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Interview'}
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

// Notes Modal Component
const NotesModal = ({ application, onClose }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState({ title: '', content: '', noteType: 'general' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadNotes();
  }, [application?.id]);

  const loadNotes = async () => {
    try {
      setLoading(true);
      const data = await getApplicationNotes(application?.id);
      setNotes(data);
    } catch (error) {
      console.error('Failed to load notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNote = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await createApplicationNote({
        applicationId: application?.id,
        ...newNote
      });
      setNewNote({ title: '', content: '', noteType: 'general' });
      setShowAddNote(false);
      loadNotes();
    } catch (error) {
      console.error('Failed to create note:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!confirm('Delete this note?')) return;
    try {
      await deleteApplicationNote(noteId);
      loadNotes();
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };

  const NOTE_TYPES = {
    general: { label: 'General', color: 'bg-gray-100 text-gray-800' },
    interview_prep: { label: 'Interview Prep', color: 'bg-purple-100 text-purple-800' },
    follow_up: { label: 'Follow Up', color: 'bg-blue-100 text-blue-800' },
    research: { label: 'Research', color: 'bg-green-100 text-green-800' },
    contact: { label: 'Contact', color: 'bg-orange-100 text-orange-800' }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Application Notes</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {application?.jobs?.title} at {application?.jobs?.companies?.name}
              </p>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Add Note Button */}
          {!showAddNote && (
            <button
              onClick={() => setShowAddNote(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 text-muted-foreground hover:text-primary transition-colors mb-4"
            >
              <Plus className="w-5 h-5" />
              Add New Note
            </button>
          )}

          {/* Add Note Form */}
          {showAddNote && (
            <form onSubmit={handleAddNote} className="bg-muted/30 rounded-lg p-4 mb-4 border border-border">
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <input
                    type="text"
                    value={newNote?.title}
                    onChange={(e) => setNewNote({ ...newNote, title: e?.target?.value })}
                    placeholder="Note title (optional)"
                    className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                  />
                  <select
                    value={newNote?.noteType}
                    onChange={(e) => setNewNote({ ...newNote, noteType: e?.target?.value })}
                    className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                  >
                    {Object.entries(NOTE_TYPES)?.map(([key, config]) => (
                      <option key={key} value={key}>{config?.label}</option>
                    ))}
                  </select>
                </div>
                <textarea
                  value={newNote?.content}
                  onChange={(e) => setNewNote({ ...newNote, content: e?.target?.value })}
                  placeholder="Write your note here..."
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                  rows={3}
                  required
                />
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddNote(false);
                      setNewNote({ title: '', content: '', noteType: 'general' });
                    }}
                    className="px-4 py-2 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Note'}
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Notes List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : notes?.length === 0 ? (
            <div className="text-center py-12">
              <StickyNote className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No notes yet. Add your first note above!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {notes?.map((note) => {
                const noteTypeConfig = NOTE_TYPES?.[note?.noteType] || NOTE_TYPES?.general;
                return (
                  <div key={note?.id} className="bg-background border border-border rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        {note?.title && (
                          <h3 className="font-semibold text-foreground mb-1">{note?.title}</h3>
                        )}
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${noteTypeConfig?.color}`}>
                          {noteTypeConfig?.label}
                        </span>
                      </div>
                      <button
                        onClick={() => handleDeleteNote(note?.id)}
                        className="text-muted-foreground hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-sm text-foreground whitespace-pre-wrap">{note?.content}</p>
                    <p className="text-xs text-muted-foreground mt-2">
                      {new Date(note?.created_at)?.toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Reminders Modal Component
const RemindersModal = ({ application, onClose }) => {
  const authContext = useAuth();
  const { user = null } = authContext || {};
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddReminder, setShowAddReminder] = useState(false);
  const [newReminder, setNewReminder] = useState({
    reminderTitle: '',
    reminderDescription: '',
    reminderType: 'follow_up',
    reminderDate: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadReminders();
  }, [application?.id]);

  const loadReminders = async () => {
    try {
      setLoading(true);
      const data = await getApplicationReminders(user?.id, application?.id);
      setReminders(data);
    } catch (error) {
      console.error('Failed to load reminders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddReminder = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      await createApplicationReminder({
        applicationId: application?.id,
        ...newReminder
      });
      setNewReminder({
        reminderTitle: '',
        reminderDescription: '',
        reminderType: 'follow_up',
        reminderDate: ''
      });
      setShowAddReminder(false);
      loadReminders();
    } catch (error) {
      console.error('Failed to create reminder:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleComplete = async (reminderId, currentStatus) => {
    try {
      await updateApplicationReminder(reminderId, { isCompleted: !currentStatus });
      loadReminders();
    } catch (error) {
      console.error('Failed to update reminder:', error);
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!confirm('Delete this reminder?')) return;
    try {
      await deleteApplicationReminder(reminderId);
      loadReminders();
    } catch (error) {
      console.error('Failed to delete reminder:', error);
    }
  };

  const REMINDER_TYPES = {
    follow_up: { label: 'Follow Up', icon: Bell, color: 'text-blue-600' },
    interview_prep: { label: 'Interview Prep', icon: Calendar, color: 'text-purple-600' },
    document_submission: { label: 'Document Submission', icon: FileText, color: 'text-green-600' },
    thank_you_note: { label: 'Thank You Note', icon: StickyNote, color: 'text-orange-600' },
    custom: { label: 'Custom', icon: Bell, color: 'text-gray-600' }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Reminders & Follow-ups</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {application?.jobs?.title} at {application?.jobs?.companies?.name}
              </p>
            </div>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {/* Add Reminder Button */}
          {!showAddReminder && (
            <button
              onClick={() => setShowAddReminder(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-border hover:border-primary hover:bg-primary/5 text-muted-foreground hover:text-primary transition-colors mb-4"
            >
              <Plus className="w-5 h-5" />
              Add New Reminder
            </button>
          )}

          {/* Add Reminder Form */}
          {showAddReminder && (
            <form onSubmit={handleAddReminder} className="bg-muted/30 rounded-lg p-4 mb-4 border border-border">
              <div className="space-y-3">
                <input
                  type="text"
                  value={newReminder?.reminderTitle}
                  onChange={(e) => setNewReminder({ ...newReminder, reminderTitle: e?.target?.value })}
                  placeholder="Reminder title"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                  required
                />
                <textarea
                  value={newReminder?.reminderDescription}
                  onChange={(e) => setNewReminder({ ...newReminder, reminderDescription: e?.target?.value })}
                  placeholder="Description (optional)"
                  className="w-full px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                  rows={2}
                />
                <div className="grid grid-cols-2 gap-3">
                  <select
                    value={newReminder?.reminderType}
                    onChange={(e) => setNewReminder({ ...newReminder, reminderType: e?.target?.value })}
                    className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                  >
                    {Object.entries(REMINDER_TYPES)?.map(([key, config]) => (
                      <option key={key} value={key}>{config?.label}</option>
                    ))}
                  </select>
                  <input
                    type="datetime-local"
                    value={newReminder?.reminderDate}
                    onChange={(e) => setNewReminder({ ...newReminder, reminderDate: e?.target?.value })}
                    className="px-3 py-2 border border-border rounded-lg bg-background text-foreground"
                    required
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddReminder(false);
                      setNewReminder({
                        reminderTitle: '',
                        reminderDescription: '',
                        reminderType: 'follow_up',
                        reminderDate: ''
                      });
                    }}
                    className="px-4 py-2 rounded-lg bg-muted text-muted-foreground hover:bg-muted/80"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Reminder'}
                  </button>
                </div>
              </div>
            </form>
          )}

          {/* Reminders List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : reminders?.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No reminders set. Add your first reminder above!</p>
            </div>
          ) : (
            <div className="space-y-3">
              {reminders?.map((reminder) => {
                const reminderTypeConfig = REMINDER_TYPES?.[reminder?.reminderType] || REMINDER_TYPES?.custom;
                const ReminderIcon = reminderTypeConfig?.icon;
                const isPast = new Date(reminder.reminderDate) < new Date();
                
                return (
                  <div
                    key={reminder?.id}
                    className={`bg-background border rounded-lg p-4 ${
                      reminder?.isCompleted
                        ? 'border-border opacity-60'
                        : isPast
                        ? 'border-red-300 bg-red-50/10' 
                        : 'border-border'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={reminder?.isCompleted}
                        onChange={() => handleToggleComplete(reminder?.id, reminder?.isCompleted)}
                        className="mt-1 w-5 h-5 rounded border-border"
                      />
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className={`font-semibold text-foreground ${reminder?.isCompleted ? 'line-through' : ''}`}>
                              {reminder?.reminderTitle}
                            </h3>
                            {reminder?.reminderDescription && (
                              <p className="text-sm text-muted-foreground mt-1">{reminder?.reminderDescription}</p>
                            )}
                          </div>
                          <button
                            onClick={() => handleDeleteReminder(reminder?.id)}
                            className="text-muted-foreground hover:text-red-600 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        <div className="flex items-center gap-3 mt-2">
                          <span className={`flex items-center gap-1 text-xs ${reminderTypeConfig?.color}`}>
                            <ReminderIcon className="w-3 h-3" />
                            {reminderTypeConfig?.label}
                          </span>
                          <span className={`text-xs ${isPast && !reminder?.isCompleted ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>
                            {new Date(reminder.reminderDate)?.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                            {isPast && !reminder?.isCompleted && ' (Overdue)'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Application Timeline Modal Component
const ApplicationTimelineModal = ({ application, onClose }) => {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTimeline = async () => {
      try {
        const history = await getApplicationStatusHistory(application?.id);
        setTimeline(history || []);
      } catch (error) {
        console.error('Failed to load timeline:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTimeline();
  }, [application?.id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-background rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden"
      >
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground">Application Timeline</h2>
              <p className="text-sm text-muted-foreground mt-1">
                {application?.jobs?.title} at {application?.jobs?.companies?.name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <XCircle className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Timeline Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(80vh-120px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : timeline?.length === 0 ? (
            <div className="text-center py-12">
              <History className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No timeline history available</p>
            </div>
          ) : (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border"></div>
              
              {/* Timeline items */}
              <div className="space-y-6">
                {timeline?.map((item, index) => {
                  const statusConfig = STATUS_CONFIG?.[item?.new_status] || STATUS_CONFIG?.pending;
                  const StatusIcon = statusConfig?.icon;
                  
                  return (
                    <div key={item?.id} className="relative pl-12">
                      {/* Timeline dot */}
                      <div className={`absolute left-0 w-8 h-8 rounded-full flex items-center justify-center ${statusConfig?.color} border-2`}>
                        <StatusIcon className="w-4 h-4" />
                      </div>
                      
                      {/* Timeline content */}
                      <div className="bg-muted/30 rounded-lg p-4 border border-border">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h3 className="font-semibold text-foreground">{statusConfig?.label}</h3>
                            <p className="text-sm text-muted-foreground">{statusConfig?.description}</p>
                          </div>
                          <span className="text-xs text-muted-foreground whitespace-nowrap ml-4">
                            {formatDate(item?.created_at)}
                          </span>
                        </div>
                        
                        {item?.notes && (
                          <div className="mt-3 pt-3 border-t border-border">
                            <p className="text-sm text-foreground">{item?.notes}</p>
                          </div>
                        )}
                        
                        {item?.changed_by_user && (
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">
                              Updated by: {item?.changed_by_user?.full_name || item?.changed_by_user?.email}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

// Application Card Component
const ApplicationCard = ({ application, onViewTimeline, onScheduleInterview, onViewNotes, onViewReminders, onAddToComparison }) => {
  const statusConfig = STATUS_CONFIG?.[application?.status] || STATUS_CONFIG?.pending;
  const StatusIcon = statusConfig?.icon;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatSalary = (min, max, type) => {
    if (!min && !max) return 'Salary not specified';
    const formatNumber = (num) => new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      maximumFractionDigits: 0 
    })?.format(num);
    
    if (min && max) {
      return `${formatNumber(min)} - ${formatNumber(max)}${type ? `/${type}` : ''}`;
    }
    return min ? `${formatNumber(min)}${type ? `/${type}` : ''}` : `${formatNumber(max)}${type ? `/${type}` : ''}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-background border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-4 flex-1">
          {/* Company Logo */}
          {application?.jobs?.companies?.logo_url ? (
            <img
              src={application?.jobs?.companies?.logo_url}
              alt={`${application?.jobs?.companies?.name} logo`}
              className="w-12 h-12 rounded-lg object-cover"
            />
          ) : (
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <Building2 className="w-6 h-6 text-primary" />
            </div>
          )}
          
          {/* Job Info */}
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-foreground mb-1">
              {application?.jobs?.title}
            </h3>
            <p className="text-sm text-muted-foreground mb-2">
              {application?.jobs?.companies?.name}
            </p>
            
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                <span>
                  {application?.jobs?.company_locations?.city}, {application?.jobs?.company_locations?.state}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Briefcase className="w-4 h-4" />
                <span>{application?.jobs?.employment_type}</span>
              </div>
            </div>
          </div>
        </div>
        
        {/* Status Badge */}
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${statusConfig?.color}`}>
          {StatusIcon && <StatusIcon className="w-4 h-4" />}
          <span className="text-sm font-medium">{statusConfig?.label}</span>
        </div>
      </div>
      {/* Salary and Experience */}
      <div className="grid grid-cols-2 gap-4 mb-4 py-4 border-t border-b border-border">
        <div>
          <p className="text-xs text-muted-foreground mb-1">Salary Range</p>
          <p className="text-sm font-medium text-foreground">
            {formatSalary(application?.jobs?.salary_min, application?.jobs?.salary_max, application?.jobs?.salary_type)}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Experience Level</p>
          <p className="text-sm font-medium text-foreground capitalize">
            {application?.jobs?.experience_level || 'Not specified'}
          </p>
        </div>
      </div>
      {/* Footer with Action Buttons */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1 text-sm text-muted-foreground">
          <Calendar className="w-4 h-4" />
          <span>Applied on {formatDate(application?.applied_at)}</span>
        </div>
        
        <div className="flex items-center gap-2">
          <button
            onClick={() => onScheduleInterview(application)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors text-sm"
            title="Schedule Interview"
          >
            <Video className="w-4 h-4" />
          </button>
          <button
            onClick={() => onViewNotes(application)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-100 text-green-700 hover:bg-green-200 transition-colors text-sm"
            title="View Notes"
          >
            <StickyNote className="w-4 h-4" />
          </button>
          <button
            onClick={() => onViewReminders(application)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-orange-100 text-orange-700 hover:bg-orange-200 transition-colors text-sm"
            title="View Reminders"
          >
            <Bell className="w-4 h-4" />
          </button>
          <button
            onClick={() => onAddToComparison(application)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors text-sm"
            title="Add to Comparison"
          >
            <GitCompare className="w-4 h-4" />
          </button>
          <button
            onClick={() => onViewTimeline(application)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors text-sm font-medium"
          >
            <History className="w-4 h-4" />
            Timeline
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Main Applications Component
const Applications = () => {
  const authContext = useAuth();
  const { user = null } = authContext || {};
  const [applications, setApplications] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showScheduleInterview, setShowScheduleInterview] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showReminders, setShowReminders] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadApplications();
      loadStatistics();
      loadAnalytics();
    }
  }, [user?.id, selectedStatus]);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const data = await fetchJobSeekerApplications(user?.id, {
        status: selectedStatus,
        sortBy: 'applied_at',
        sortOrder: 'desc'
      });
      setApplications(data);
    } catch (error) {
      console.error('Failed to load applications:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      const stats = await getJobSeekerApplicationStatistics(user?.id);
      setStatistics(stats);
    } catch (error) {
      console.error('Failed to load statistics:', error);
    }
  };

  const loadAnalytics = async () => {
    try {
      const data = await getApplicationAnalytics(user?.id);
      setAnalytics(data);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };

  const handleViewTimeline = (application) => {
    setSelectedApplication(application);
    setShowTimeline(true);
  };

  const handleScheduleInterview = (application) => {
    setSelectedApplication(application);
    setShowScheduleInterview(true);
  };

  const handleViewNotes = (application) => {
    setSelectedApplication(application);
    setShowNotes(true);
  };

  const handleViewReminders = (application) => {
    setSelectedApplication(application);
    setShowReminders(true);
  };

  const handleAddToComparison = async (application) => {
    // TODO: Implement comparison set selection modal
    console.log('Add to comparison:', application);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">My Applications</h1>
        <p className="text-muted-foreground">
          Track your job applications with detailed analytics and management tools
        </p>
      </div>
      {/* Analytics Dashboard */}
      {analytics && statistics && (
        <AnalyticsDashboard analytics={analytics} statistics={statistics} />
      )}
      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-background border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Applications</p>
                <p className="text-2xl font-bold text-foreground mt-1">{statistics?.total}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-blue-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-background border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-foreground mt-1">{statistics?.active}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-background border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">This Week</p>
                <p className="text-2xl font-bold text-foreground mt-1">{statistics?.thisWeek}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                <Calendar className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-background border border-border rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Interviews</p>
                <p className="text-2xl font-bold text-foreground mt-1">{statistics?.byStatus?.interview}</p>
              </div>
              <div className="w-12 h-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <Clock className="w-6 h-6 text-yellow-600" />
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Filter Bar */}
      <div className="bg-background border border-border rounded-lg p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-muted-foreground" />
            <span className="text-sm font-medium text-foreground">Filter by Status:</span>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => setSelectedStatus('all')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedStatus === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              All Applications
            </button>
            
            {Object.entries(STATUS_CONFIG)?.map(([key, config]) => {
              const Icon = config?.icon;
              const count = statistics?.byStatus?.[key] || 0;
              
              return (
                <button
                  key={key}
                  onClick={() => setSelectedStatus(key)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedStatus === key
                      ? config?.color
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{config?.label}</span>
                  <span className="ml-1 px-2 py-0.5 rounded-full bg-background text-xs">
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {/* Applications List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : applications?.length === 0 ? (
        <div className="bg-muted/20 border border-border rounded-lg p-12 text-center">
          <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-foreground mb-2">No Applications Found</h3>
          <p className="text-muted-foreground mb-6">
            {selectedStatus === 'all' ? "You haven't applied to any jobs yet. Start exploring opportunities!" : `No applications with status "${STATUS_CONFIG?.[selectedStatus]?.label}"`
            }
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {applications?.map((application) => (
            <ApplicationCard
              key={application?.id}
              application={application}
              onViewTimeline={handleViewTimeline}
              onScheduleInterview={handleScheduleInterview}
              onViewNotes={handleViewNotes}
              onViewReminders={handleViewReminders}
              onAddToComparison={handleAddToComparison}
            />
          ))}
        </div>
      )}
      {/* Timeline Modal */}
      {showTimeline && selectedApplication && (
        <ApplicationTimelineModal
          application={selectedApplication}
          onClose={() => {
            setShowTimeline(false);
            setSelectedApplication(null);
          }}
        />
      )}
      {/* Schedule Interview Modal */}
      {showScheduleInterview && selectedApplication && (
        <InterviewScheduleModal
          application={selectedApplication}
          onClose={() => {
            setShowScheduleInterview(false);
            setSelectedApplication(null);
          }}
          onSave={loadApplications}
        />
      )}
      {/* Notes Modal */}
      {showNotes && selectedApplication && (
        <NotesModal
          application={selectedApplication}
          onClose={() => {
            setShowNotes(false);
            setSelectedApplication(null);
          }}
        />
      )}
      {/* Reminders Modal */}
      {showReminders && selectedApplication && (
        <RemindersModal
          application={selectedApplication}
          onClose={() => {
            setShowReminders(false);
            setSelectedApplication(null);
          }}
        />
      )}
    </motion.div>
  );
};

export default Applications;